import React, { useState, useMemo } from 'react';
import Select from '../../../ui/Select';
import '../DeckEditor.css';

// ========== HYPERGEOMETRISCHE VERTEILUNG ==========

/** Log-Binomialkoeffizient ln(C(n, k)) für stabile Berechnung */
function logComb(n, k) {
  if (k < 0 || k > n) return -Infinity;
  if (k === 0 || k === n) return 0;
  if (k > n - k) k = n - k;
  let result = 0;
  for (let i = 0; i < k; i++) {
    result += Math.log(n - i) - Math.log(i + 1);
  }
  return result;
}

/**
 * P(X >= minDesired) bei hypergeometrischer Verteilung.
 * N = Populationsgröße (Deck), K = Erfolge in Population,
 * n = Stichprobe (gezogene Karten), k = mindestens gewünscht.
 */
function hypergeometricAtLeast(N, K, n, k) {
  if (N <= 0 || n <= 0 || K <= 0 || k <= 0) return k <= 0 ? 1 : 0;
  if (k > Math.min(K, n)) return 0;
  if (n > N) return 0;

  // P(X >= k) = 1 - Σ P(X = i) für i = 0..k-1
  let cumulative = 0;
  for (let i = 0; i < k; i++) {
    const logP = logComb(K, i) + logComb(N - K, n - i) - logComb(N, n);
    cumulative += Math.exp(logP);
  }
  return Math.max(0, Math.min(1, 1 - cumulative));
}

// ========== GRUPPIERUNGSOPTIONEN ==========

const GROUP_OPTIONS = [
  { value: 'category', label: 'Kategorie' },
  { value: 'type', label: 'Typ' },
  { value: 'subtype', label: 'Subtyp' },
  { value: 'cmc', label: 'Manakosten' },
];

/** Macht jeden Wortanfang groß */
function titleCase(str) {
  return str.replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Extrahiert den Gruppierungswert einer Karte */
function getGroupValue(card, groupBy) {
  switch (groupBy) {
    case 'category':
      return card.category || 'other';
    case 'type':
      return (card.types && card.types.length > 0) ? card.types : ['Sonstige'];
    case 'subtype':
      // Karten ohne Subtypen nicht aufführen
      return (card.subTypes && card.subTypes.length > 0) ? card.subTypes : null;
    case 'cmc': {
      // Länder ausschließen, aber MDFCs (Land + Spell) beibehalten
      const types = card.types || [];
      let isLand = types.some((t) => t.toLowerCase() === 'land');
      if (isLand) {
        const isMDFC = card.imageUrlBack != null;
        isLand = !isMDFC;
      }
      if (isLand) return null;
      return [String(Math.floor(card.cmc || 0))];
    }
    default:
      return ['Sonstige'];
  }
}

// ========== KOMPONENTE ==========

export default function HypergeometricStats({ cards = [], excludedCategoryNames = new Set() }) {
  const [groupBy, setGroupBy] = useState('type');
  const [drawCount, setDrawCount] = useState(7);
  const [minDesired, setMinDesired] = useState(1);

  // Commander- und ausgeschlossene Karten herausfiltern
  const filteredCards = useMemo(
    () => cards.filter((c) => {
      if (c.isCommander) return false;
      const cat = (c.category || '').toLowerCase();
      if (excludedCategoryNames.has(cat)) return false;
      return true;
    }),
    [cards, excludedCategoryNames]
  );

  // Gesamtanzahl Karten im Deck (mit Quantity, ohne Excluded-Kategorien)
  const totalCards = useMemo(
    () => filteredCards.reduce((sum, c) => sum + (c.quantity || 1), 0),
    [filteredCards]
  );

  // Gruppierung berechnen
  const groups = useMemo(() => {
    const map = {};
    for (const card of filteredCards) {
      const qty = card.quantity || 1;
      const values = getGroupValue(card, groupBy);
      // null = Karte soll nicht aufgeführt werden (z.B. Subtype ohne Wert)
      if (values === null) continue;
      // category gibt einen einzelnen String zurück, type/subtype/cmc ein Array
      const arr = Array.isArray(values) ? values : [values];
      for (const val of arr) {
        const key = String(val).trim();
        if (!key) continue;
        map[key] = (map[key] || 0) + qty;
      }
    }
    // Sortiert nach Anzahl absteigend, mit Title Case für Anzeige
    return Object.entries(map)
      .map(([name, count]) => ({ name: titleCase(name), count }))
      .sort((a, b) => b.count - a.count);
  }, [filteredCards, groupBy]);

  // Wahrscheinlichkeiten berechnen
  const rows = useMemo(() => {
    const n = Math.max(0, Math.min(drawCount, totalCards));
    const k = Math.max(0, minDesired);
    return groups.map((g) => ({
      ...g,
      probability: hypergeometricAtLeast(totalCards, g.count, n, k),
    }));
  }, [groups, totalCards, drawCount, minDesired]);

  const inputStyle = {
    width: '40px',
    boxSizing: 'border-box',
    flexShrink: 0,
    padding: '2px 4px',
    borderRadius: '4px',
    border: '1px solid var(--border)',
    backgroundColor: 'var(--bg-input)',
    color: 'var(--text-primary)',
    fontSize: '0.85rem',
    textAlign: 'center',
    outline: 'none',
  };

  const labelStyle = {
    color: 'var(--text-secondary)',
    fontSize: '0.8rem',
    whiteSpace: 'nowrap',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Konfiguration - bevorzugt eine Zeile, bricht in schmalen Kacheln um:
          "nowrap" ließe die Gruppen bei schmalen Kachelbreiten über den
          Kachelrand hinaus in die Nachbarkachel ragen. Die Gruppierungs-Zeile
          ist der einzige schrumpfbare Rest (minWidth:0, Select ohne eigenen
          Boden — sein Text darf ellipsen) — Ziehe/Min. behalten ihre feste
          Breite (40px-Feld). Bei 290px Brücken-Innenbreite passen alle drei
          rechnerisch in eine Zeile (siehe CLAUDE.md/Report — Kalkulation,
          keine Browser-Messung in diesem Auftrag). */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          rowGap: '4px',
          marginBottom: '6px',
          flexWrap: 'wrap',
        }}
      >
        <label style={{ ...labelStyle, flex: '1 1 auto', minWidth: 0 }}>
          Gruppen
          <Select
            value={groupBy}
            onChange={setGroupBy}
            options={GROUP_OPTIONS}
            size="sm"
            style={{ flex: '1 1 auto', minWidth: 0 }}
          />
        </label>

        <label style={labelStyle}>
          Ziehe
          <input
            type="number"
            min={1}
            max={totalCards || 99}
            value={drawCount}
            onChange={(e) => setDrawCount(Math.max(1, parseInt(e.target.value, 10) || 1))}
            style={inputStyle}
          />
        </label>

        <label style={labelStyle}>
          Min.
          <input
            type="number"
            min={0}
            max={drawCount}
            value={minDesired}
            onChange={(e) => setMinDesired(Math.max(0, parseInt(e.target.value, 10) || 0))}
            style={inputStyle}
          />
        </label>
      </div>

      {/* Ergebnis-Tabelle */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          borderRadius: '4px',
          border: '1px solid var(--border-subtle)',
        }}
      >
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '0.85rem',
            color: 'var(--text-primary)',
          }}
        >
          <thead>
            <tr style={{ position: 'sticky', top: 0, backgroundColor: 'var(--bg-surface)' }}>
              <th style={thStyle}>
                {GROUP_OPTIONS.find((o) => o.value === groupBy)?.label || 'Gruppe'}
              </th>
              <th style={{ ...thStyle, textAlign: 'center', width: '40px' }}>Anz.</th>
              <th style={{ ...thStyle, textAlign: 'right', width: '60px' }}>P(≥{minDesired})</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.name} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                <td style={tdStyle}>{row.name}</td>
                <td style={{ ...tdStyle, textAlign: 'center' }}>{row.count}</td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>
                  {(row.probability * 100).toFixed(1)}%
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={3} style={{ ...tdStyle, textAlign: 'center', color: 'var(--text-muted)' }}>
                  Keine Karten
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* <div style={{ color: '#666', fontSize: '0.7rem', marginTop: '4px', textAlign: 'right' }}>
        {totalCards} Karten im Deck
      </div> */}
    </div>
  );
}

// ========== TABELLEN-STYLES ==========

const thStyle = {
  padding: '5px 8px',
  textAlign: 'left',
  fontWeight: 600,
  fontSize: '0.8rem',
  color: 'var(--text-secondary)',
  borderBottom: '1px solid var(--border)',
};

const tdStyle = {
  padding: '4px 8px',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  maxWidth: '160px',
};
